/**
 * JSON Patch Operations Utility
 * Generates RFC 6902 compliant JSON Patch operations by comparing two objects
 */

import type { JsonPatchOperation } from '@/types/configuration'

/**
 * Deep clone an object to avoid reference issues
 */
function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj.getTime()) as T
  if (Array.isArray(obj)) return obj.map(item => deepClone(item)) as T
  return Object.keys(obj).reduce((cloned, key) => {
    cloned[key] = deepClone((obj as any)[key])
    return cloned
  }, {} as any) as T
}

/**
 * Check if two values are equal (deep comparison for objects)
 */
function isEqual(a: any, b: any): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  if (a === undefined || b === undefined) return false
  if (typeof a !== typeof b) return false
  
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => isEqual(item, b[index]))
  }
  
  if (typeof a === 'object') {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    if (keysA.length !== keysB.length) return false
    return keysA.every(key => keysB.includes(key) && isEqual(a[key], b[key]))
  }
  
  return false
}

/**
 * Escape JSON Pointer path components according to RFC 6901
 */
function escapeJsonPointer(str: string): string {
  return str.replace(/~/g, '~0').replace(/\//g, '~1')
}

/**
 * Generate JSON path from array of keys
 */
function generatePath(pathArray: (string | number)[]): string {
  if (pathArray.length === 0) return ''
  return '/' + pathArray.map(key => escapeJsonPointer(String(key))).join('/')
}

/**
 * Generate JSON Patch operations by comparing original and current objects
 */
export function generateJsonPatchOperations(
  original: any, 
  current: any, 
  basePath: (string | number)[] = []
): JsonPatchOperation[] {
  const operations: JsonPatchOperation[] = []
  
  // Handle null/undefined cases
  if (original === null || original === undefined) {
    if (current !== null && current !== undefined) {
      operations.push({
        op: 'add',
        path: generatePath(basePath),
        value: deepClone(current)
      })
    }
    return operations
  }
  
  if (current === null || current === undefined) {
    operations.push({
      op: 'remove',
      path: generatePath(basePath)
    })
    return operations
  }
  
  // Handle primitive values
  if (typeof original !== 'object' || typeof current !== 'object') {
    if (!isEqual(original, current)) {
      operations.push({
        op: 'replace',
        path: generatePath(basePath),
        value: deepClone(current)
      })
    }
    return operations
  }
  
  // Handle arrays
  if (Array.isArray(original) && Array.isArray(current)) {
    // For arrays, we'll use a simple approach: replace if different
    // More sophisticated array diffing could be implemented later
    if (!isEqual(original, current)) {
      operations.push({
        op: 'replace',
        path: generatePath(basePath),
        value: deepClone(current)
      })
    }
    return operations
  }
  
  // Handle objects
  if (Array.isArray(original) || Array.isArray(current)) {
    // Type mismatch (object vs array)
    operations.push({
      op: 'replace',
      path: generatePath(basePath),
      value: deepClone(current)
    })
    return operations
  }
  
  // Compare object properties
  const originalKeys = new Set(Object.keys(original))
  const currentKeys = new Set(Object.keys(current))
  
  // Handle removed properties
  for (const key of originalKeys) {
    if (!currentKeys.has(key)) {
      operations.push({
        op: 'remove',
        path: generatePath([...basePath, key])
      })
    }
  }
  
  // Handle added and modified properties
  for (const key of currentKeys) {
    const newPath = [...basePath, key]
    
    if (!originalKeys.has(key)) {
      // Added property
      operations.push({
        op: 'add',
        path: generatePath(newPath),
        value: deepClone(current[key])
      })
    } else {
      // Potentially modified property - recurse
      const nestedOps = generateJsonPatchOperations(
        original[key],
        current[key],
        newPath
      )
      operations.push(...nestedOps)
    }
  }
  
  return operations
}

/**
 * Validate JSON Patch operations format
 */
export function validateJsonPatchOperations(operations: JsonPatchOperation[]): boolean {
  return operations.every(op => {
    // Required fields
    if (!op.op || !op.path) return false
    
    // Valid operations
    if (!['add', 'remove', 'replace', 'move', 'copy', 'test'].includes(op.op)) return false
    
    // Path must start with / (except for root)
    if (op.path !== '' && !op.path.startsWith('/')) return false
    
    // Value required for add/replace operations
    if ((op.op === 'add' || op.op === 'replace') && op.value === undefined) return false
    
    // From required for move/copy operations
    if ((op.op === 'move' || op.op === 'copy') && !op.from) return false
    
    return true
  })
}

/**
 * Optimize JSON Patch operations by removing redundant operations
 */
export function optimizeJsonPatchOperations(operations: JsonPatchOperation[]): JsonPatchOperation[] {
  // Remove operations that cancel each other out
  // This is a simple implementation - more sophisticated optimization could be added
  const optimized: JsonPatchOperation[] = []
  
  for (const op of operations) {
    // Skip test operations in optimization (they're usually for validation)
    if (op.op === 'test') {
      optimized.push(op)
      continue
    }
    
    // Check if this operation is redundant with a previous one
    const existingIndex = optimized.findIndex(existing => 
      existing.path === op.path && existing.op !== 'test'
    )
    
    if (existingIndex >= 0) {
      // Replace existing operation with the latest one
      optimized[existingIndex] = op
    } else {
      optimized.push(op)
    }
  }
  
  return optimized
}

/**
 * Create a JSON Patch operation for testing a value (useful for conflict detection)
 */
export function createTestOperation(path: string, value: any): JsonPatchOperation {
  return {
    op: 'test',
    path,
    value: deepClone(value)
  }
}
